import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Network } from 'lucide-react';
import { cn } from '../utils/cn';
import { navigation } from '../config/navigation';
import { supabase } from '../lib/supabase';

interface NavItemProps {
  item: any;
  isCollapsed: boolean;
  isActive: boolean;
}

function NavItem({ item, isCollapsed, isActive }: NavItemProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  
  const handleClick = async () => {
    if (item.action === 'signOut') {
      try {
        await supabase.auth.signOut();
        navigate('/');
      } catch (error) {
        console.error('Error signing out:', error);
      }
      return;
    }
  };
  
  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "w-full flex items-center justify-between px-4 py-2 text-sm rounded-md",
            isActive ? "bg-primary-500/10 text-primary-500" : "text-gray-300 hover:text-white hover:bg-dark-700",
            isCollapsed && "justify-center"
          )}
        >
          <div className="flex items-center">
            {item.icon && <item.icon className={cn("w-5 h-5 flex-shrink-0", !isCollapsed && "mr-3")} />}
            {!isCollapsed && item.name}
          </div>
          {!isCollapsed && (
            <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
          )}
        </button>
        
        {isOpen && !isCollapsed && (
          <div className="mt-1 space-y-1">
            {item.children.map((child: any) => (
              <Link
                key={child.name}
                to={child.href}
                className={cn(
                  "block px-4 py-2 text-sm rounded-md ml-7",
                  location.pathname === child.href
                    ? "bg-primary-500/10 text-primary-500"
                    : "text-gray-300 hover:text-white hover:bg-dark-700"
                )}
              >
                {child.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return item.action ? (
    <button
      onClick={handleClick}
      className={cn(
        "w-full flex items-center px-4 py-2 text-sm rounded-md",
        isActive ? "bg-primary-500/10 text-primary-500" : "text-gray-300 hover:text-white hover:bg-dark-700",
        isCollapsed && "justify-center"
      )}
    >
      {item.icon && <item.icon className={cn("w-5 h-5 flex-shrink-0", !isCollapsed && "mr-3")} />}
      {!isCollapsed && item.name}
    </button>
  ) : (
    <Link
      to={item.href}
      className={cn(
        "flex items-center px-4 py-2 text-sm rounded-md",
        isActive ? "bg-primary-500/10 text-primary-500" : "text-gray-300 hover:text-white hover:bg-dark-700",
        isCollapsed && "justify-center"
      )}
    >
      {item.icon && <item.icon className={cn("w-5 h-5 flex-shrink-0", !isCollapsed && "mr-3")} />}
      {!isCollapsed && item.name}
    </Link>
  );
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  hasWarningBanner?: boolean;
}

export default function Sidebar({ isOpen, onClose, onToggle, hasWarningBanner }: SidebarProps) {
  const location = useLocation();
  const isCollapsed = !isOpen;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "hidden lg:fixed lg:flex lg:flex-col border-r border-dark-700",
        hasWarningBanner ? "top-9" : "top-0",
        "left-0 bottom-0",
        isCollapsed ? "lg:w-20" : "lg:w-64"
      )}>
        <div className="flex flex-grow flex-col overflow-y-auto bg-dark-800">
          {/* Logo section with bottom border */}
          <div className={cn(
            "flex h-16 flex-shrink-0 items-center border-b border-dark-700",
            isCollapsed ? "justify-center px-2" : "px-4"
          )}>
            <h1 className="text-xl font-bold text-primary-500 flex items-center gap-2">
            <Link 
              to="/"
              className="flex items-center gap-2"
            >
              <Network className="w-6 h-6 text-primary-500" />
              {!isCollapsed && (
                <span className="text-xl font-bold text-primary-500">
                  NRM
                </span>
              )}
            </Link>
            </h1>
          </div>

          {/* Main navigation - top section */}
          <nav className="flex-1 space-y-1 px-2 py-4 border-b border-dark-700">
            {navigation.filter(item => !item.isBottom).map((item) => (
              <NavItem
                key={item.name}
                item={item}
                isCollapsed={isCollapsed}
                isActive={
                  item.children
                    ? item.children.some((child: any) => child.href === location.pathname)
                    : item.href === location.pathname
                }
              />
            ))}
          </nav>

          {/* Bottom section */}
          <div className="px-2 py-4 space-y-1">
            <div className="px-4 py-3">
              <p className="text-sm text-gray-400">waparrish@gmail.com</p>
            </div>
            {navigation.filter(item => item.isBottom).map((item) => (
              <NavItem
                key={item.name}
                item={item}
                isCollapsed={isCollapsed}
                isActive={item.href === location.pathname}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}